import React, { useEffect, useState } from "react";
import { Star, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { TESTIMONIALS, GOOGLE_REVIEWS } from "../../lib/constants";

// Google "G" wordmark colors
const GoogleLogo = ({ size = 18 }) => (
  <span
    className="inline-flex items-baseline gap-[1px] font-semibold"
    style={{ fontFamily: "'Inter', 'Outfit', sans-serif", fontSize: size, lineHeight: 1 }}
    aria-label="Google"
  >
    <span style={{ color: "#4285F4" }}>G</span>
    <span style={{ color: "#EA4335" }}>o</span>
    <span style={{ color: "#FBBC05" }}>o</span>
    <span style={{ color: "#4285F4" }}>g</span>
    <span style={{ color: "#34A853" }}>l</span>
    <span style={{ color: "#EA4335" }}>e</span>
  </span>
);

const Stars = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${n} star rating`}>
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={16} fill="#FBBC05" strokeWidth={0} />
    ))}
  </div>
);

const initials = (name) =>
  name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

export const Testimonials = () => {
  const [start, setStart] = useState(0);
  const [autoplay, setAutoplay] = useState(true);
  const total = TESTIMONIALS.length;

  // Auto-rotate one card at a time every 5s
  useEffect(() => {
    if (!autoplay) return;
    const id = setInterval(() => setStart((s) => (s + 1) % total), 5000);
    return () => clearInterval(id);
  }, [autoplay, total]);

  // Show 3 cards on desktop, 1 on mobile — grid handles layout;
  // We reorder the array so `start` sits first.
  const visible = [...TESTIMONIALS.slice(start), ...TESTIMONIALS.slice(0, start)];

  const next = () => setStart((s) => (s + 1) % total);
  const prev = () => setStart((s) => (s - 1 + total) % total);

  return (
    <section
      className="py-24 sm:py-32 bg-ink text-white relative overflow-hidden"
      data-testid="testimonials"
    >
      <div className="grain absolute inset-0 opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6">
        {/* Header — Google-branded rating summary */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-brand mb-5">
              <GoogleLogo size={14} />
              <span className="opacity-60">·</span>
              <span>Verified Reviews</span>
            </div>
            <h2 className="font-serif text-5xl sm:text-6xl leading-[0.95] tracking-tight">
              What DC patients are saying <em className="text-brand">about DWC</em>.
            </h2>
          </div>

          <div className="flex items-center gap-8">
            <div className="flex flex-col items-start">
              <div className="flex items-baseline gap-2">
                <span className="font-serif text-6xl text-brand leading-none">
                  {GOOGLE_REVIEWS.rating.toFixed(1)}
                </span>
                <span className="text-white/50 text-lg">/ 5</span>
              </div>
              <div className="mt-2">
                <Stars n={5} />
              </div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.25em] text-white/50">
                Based on {GOOGLE_REVIEWS.reviewCount}+ Google reviews
              </div>
            </div>
            <div className="hidden sm:block h-16 w-px bg-white/10" />
            <a
              href={GOOGLE_REVIEWS.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-brand hover:text-brand px-5 py-3 text-[11px] uppercase tracking-[0.22em] transition-colors"
              data-testid="reviews-see-all"
            >
              See all on <GoogleLogo size={12} />
              <ArrowUpRight size={13} />
            </a>
          </div>
        </div>

        {/* Cards */}
        <div
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
          onMouseEnter={() => setAutoplay(false)}
          onMouseLeave={() => setAutoplay(true)}
          data-testid="reviews-carousel"
        >
          {visible.slice(0, 3).map((t, i) => (
            <article
              key={`${t.name}-${start}-${i}`}
              className="border border-white/10 p-8 bg-white/[0.02] hover:border-brand transition-all animate-fade-in"
              data-testid={`review-card-${i}`}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand text-white flex items-center justify-center font-serif text-sm">
                    {initials(t.name)}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                      {t.neighborhood} · {t.date}
                    </div>
                  </div>
                </div>
                <GoogleLogo size={11} />
              </div>
              <Stars n={5} />
              <p className="mt-4 text-white/85 leading-relaxed text-[15px]">
                &ldquo;{t.quote}&rdquo;
              </p>
            </article>
          ))}
        </div>

        {/* Controls */}
        <div className="mt-8 flex items-center justify-between">
          <div className="flex items-center gap-1.5" data-testid="reviews-dots">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setStart(i)}
                aria-label={`Show review ${i + 1}`}
                className={`h-1 rounded-full transition-all ${
                  i === start ? "w-8 bg-brand" : "w-3 bg-white/25 hover:bg-white/50"
                }`}
                data-testid={`reviews-dot-${i}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              aria-label="Previous review"
              className="w-10 h-10 border border-white/15 hover:border-brand hover:text-brand flex items-center justify-center transition-colors"
              data-testid="reviews-prev"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="Next review"
              className="w-10 h-10 border border-white/15 hover:border-brand hover:text-brand flex items-center justify-center transition-colors"
              data-testid="reviews-next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Write a review CTA */}
        <div className="mt-14 pt-10 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div className="max-w-xl">
            <div className="font-serif text-2xl leading-tight">
              Loved your DWC visit? <em className="text-brand">Tell Google.</em>
            </div>
            <p className="text-sm text-white/60 mt-2 leading-relaxed">
              Honest reviews help other DC patients find our Petworth dispensary at 4915 Georgia Ave NW, 20011.
              We never offer incentives for reviews — just gratitude.
            </p>
          </div>
          <a
            href={GOOGLE_REVIEWS.mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-6 py-4 text-[11px] uppercase tracking-[0.22em] transition-colors shrink-0"
            data-testid="reviews-write-cta"
          >
            Write a Google review <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
